import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AXC_CSS, Field } from '@/pages/admin/AxiomOnboardingModal';
import {
  getIntegrationBanking,
  updateIntegrationBanking,
  type IntegrationBanking,
} from '@/services/integrations.service';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';

/**
 * Clinic-managed ACH details for an integration that bills by direct debit.
 *
 * WHY IT RE-ASKS FOR EVERYTHING. The stored routing/account numbers are never
 * returned to the browser — the API answers with last-4 only — so this form
 * starts empty by design rather than prefilling secrets it cannot read. That
 * also matches what the change actually is: authorizing debits against a new
 * account, which is a fresh attestation, not an edit of four characters.
 */
interface AxiomBankingModalProps {
  integrationKey: string;
  vendorName: string;
  open: boolean;
  onClose: () => void;
  onSaved?: (result: { synced?: boolean; syncError?: string }) => void;
}

interface BankForm {
  bankName: string;
  accountHolderName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber: string;
}

const EMPTY: BankForm = {
  bankName: '',
  accountHolderName: '',
  accountType: 'checking',
  routingNumber: '',
  accountNumber: '',
  confirmAccountNumber: '',
};

/** The ABA checksum the API enforces, mirrored here so a typo is caught before
 *  a round trip. Kept in sync with IntegrationsController::validAba(). */
function validAba(routing: string): boolean {
  const d = routing.replace(/[^0-9]/g, '');
  if (d.length !== 9) return false;
  const n = (i: number) => Number(d[i]);
  const sum =
    3 * (n(0) + n(3) + n(6)) + 7 * (n(1) + n(4) + n(7)) + 1 * (n(2) + n(5) + n(8));
  return sum > 0 && sum % 10 === 0;
}

export function AxiomBankingModal({
  integrationKey,
  vendorName,
  open,
  onClose,
  onSaved,
}: AxiomBankingModalProps) {
  // Locked page (plan 2026-10-10-001 U7: R8, R18): the page offers no way in,
  // and the modal itself renders nothing and fetches nothing, so no Locked
  // path can reach a banking write.
  const locked = useAdminPageLevel() === 'locked';
  const qc = useQueryClient();
  const [f, setF] = useState<BankForm>(EMPTY);
  const [authorized, setAuthorized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: current } = useQuery<IntegrationBanking>({
    queryKey: ['integration-banking', integrationKey],
    queryFn: () => getIntegrationBanking(integrationKey),
    enabled: open && !locked,
  });

  // Every open starts clean: no secret is left in component state between
  // sessions, and a half-typed abandoned edit never reappears prefilled.
  useEffect(() => {
    if (open) {
      setF(EMPTY);
      setAuthorized(false);
      setError(null);
    }
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      updateIntegrationBanking(integrationKey, {
        bankName: f.bankName.trim(),
        accountHolderName: f.accountHolderName.trim(),
        accountType: f.accountType,
        routingNumber: f.routingNumber.replace(/[^0-9]/g, ''),
        accountNumber: f.accountNumber.replace(/[^0-9]/g, ''),
        achAuthorization: true,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['integration-banking', integrationKey] });
      onSaved?.({ synced: res.synced, syncError: res.syncError });
      onClose();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  if (!open || locked) return null;

  const set = <K extends keyof BankForm>(k: K, v: BankForm[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  const submit = () => {
    setError(null);
    for (const [k, lbl] of [
      ['accountHolderName', "Bank Account Holder's Name"],
      ['bankName', 'Bank name'],
      ['routingNumber', 'Routing number'],
      ['accountNumber', 'Account number'],
    ] as const) {
      if (!f[k].trim()) {
        setError(`${lbl} is required.`);
        return;
      }
    }
    if (!validAba(f.routingNumber)) {
      setError('That routing number is not valid. Check the 9-digit ABA number on your check.');
      return;
    }
    const acct = f.accountNumber.replace(/[^0-9]/g, '');
    if (acct.length < 4 || acct.length > 17) {
      setError('That account number is not valid.');
      return;
    }
    // Typing it twice is the only check available: nothing on our side knows
    // the real number, and a wrong one fails as a returned debit weeks later.
    if (acct !== f.confirmAccountNumber.replace(/[^0-9]/g, '')) {
      setError('The account numbers do not match.');
      return;
    }
    if (!authorized) {
      setError('Please authorize ACH debits for the new account.');
      return;
    }
    save.mutate();
  };

  return (
    <div className="axc-root">
      <style>{AXC_CSS}</style>
      <div className="axc-overlay" onClick={save.isPending ? undefined : onClose}>
        <div
          className="axc-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`Update ${vendorName} banking details`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="axc-modal-head">
            <div className="axc-brand">
              <div className="axc-mark" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  <path d="M8 13l2.5 2.5L16 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <div className="axc-brand-name">Banking details</div>
                <div className="axc-brand-sub">{vendorName} Connect · ACH billing</div>
              </div>
            </div>
            <button className="axc-close" aria-label="Close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="axc-modal-body">
            {error && <div className="axc-err-line" style={{ marginBottom: 12 }}>{error}</div>}

            {current?.exists && (current.accountLast4 || current.bankName) && (
              <p style={{ fontSize: 13, color: '#6B7C8C', marginBottom: 14, lineHeight: 1.5 }}>
                Currently billing {current.bankName || 'your bank'}
                {current.accountLast4 ? ` account ••••${current.accountLast4}` : ''}. Entering new
                details replaces it for all future charges.
              </p>
            )}

            <div className="axc-subhead">
              New bank account (ACH) <span className="axc-mt-badge">Encrypted</span>
            </div>
            <div className="axc-fields" style={{ marginTop: 12 }}>
              <Field
                label="Bank Account Holder's Name"
                required
                value={f.accountHolderName}
                onChange={(v) => set('accountHolderName', v)}
                hint="Exactly as it appears on the bank statement — a person or the legal entity."
              />
              <div className="axc-row" style={{ display: 'flex', gap: 14 }}>
                <Field label="Bank name" required value={f.bankName} onChange={(v) => set('bankName', v)} />
                <div className="axc-field" style={{ flex: '0 0 auto' }}>
                  <label className="axc-label" htmlFor="axc-account-type">
                    Account type<span className="axc-req">*</span>
                  </label>
                  <select
                    id="axc-account-type"
                    value={f.accountType}
                    onChange={(e) => set('accountType', e.target.value as 'checking' | 'savings')}
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
              </div>
              <div className="axc-row" style={{ display: 'flex', gap: 14 }}>
                <Field
                  label="Routing number"
                  required
                  value={f.routingNumber}
                  onChange={(v) => set('routingNumber', v)}
                  inputMode="numeric"
                />
                <Field
                  label="Account number"
                  required
                  value={f.accountNumber}
                  onChange={(v) => set('accountNumber', v)}
                  inputMode="numeric"
                />
              </div>
              <Field
                label="Confirm account number"
                required
                value={f.confirmAccountNumber}
                onChange={(v) => set('confirmAccountNumber', v)}
                inputMode="numeric"
              />

              <label
                className={`axc-check axc-auth ${error && !authorized ? 'has-error' : ''}`}
                style={{ marginTop: 4 }}
              >
                <input
                  type="checkbox"
                  checked={authorized}
                  onChange={(e) => setAuthorized(e.target.checked)}
                />
                <span>
                  I authorize {vendorName} to debit the account above for orders placed by this
                  clinic, and confirm I am authorized to act on it. This authorization remains in
                  effect until revoked in writing.
                </span>
              </label>
            </div>
          </div>

          <div className="axc-modal-foot">
            <div className="axc-foot-left">
              <button className="axc-btn-ghost" onClick={onClose} disabled={save.isPending}>
                Cancel
              </button>
            </div>
            <div className="axc-foot-right">
              <button className="axc-btn-primary" onClick={submit} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save banking details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
